import axios, { type AxiosError } from "axios";
import { encryptLoginPassword } from "@/utils/passwordCrypto";
import { clearSession, getStoredToken, setSession, type LoginResult, type MerchantUser } from "./session";

type ApiSuccess<T> = { success: true; data: T };
type ApiFailure = { success: false; errorCode: number; errorMsg: string };

/** 按商户前端域名映射后端 API；本地/未知域名回退到 VITE_API_BASE_URL（空则走同源 / Vite 代理） */
function resolveApiBaseUrl(): string {
  const fromEnv = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
  if (typeof window === "undefined") return fromEnv;

  const host = window.location.hostname.toLowerCase();
  const byHost: Record<string, string> = {
    "dashboard.dvaplus.net": "https://topup.dvaplus.net",
    "dashboarddev.dvaplus.net": "https://topupdev.dvaplus.net",
  };
  return byHost[host] ?? fromEnv;
}

const baseURL = resolveApiBaseUrl();

export const api = axios.create({
  baseURL,
  timeout: 20000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function loginPath(): string {
  const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
  return `${base}/login`;
}

function isOnLoginPage(): boolean {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  return path.endsWith("/login");
}

async function readApiFailure(err: AxiosError): Promise<ApiFailure | null> {
  const data = err.response?.data;
  if (!data) return null;
  if (typeof data === "object" && !(data instanceof Blob) && "errorCode" in data) {
    return data as ApiFailure;
  }
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()) as ApiFailure;
    } catch {
      return null;
    }
  }
  return null;
}

function redirectToLoginOnSessionExpired(failure: ApiFailure | null) {
  if (!failure || failure.success !== false) return;
  if (failure.errorCode !== 401) return;
  if (failure.errorMsg !== "会话已过期") return;
  clearSession();
  if (!isOnLoginPage()) {
    window.location.replace(loginPath());
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const failure = await readApiFailure(error);
    redirectToLoginOnSessionExpired(failure);
    return Promise.reject(error);
  }
);

function extractErrorMessage(err: unknown): string {
  const ax = err as AxiosError<ApiFailure>;
  return ax.response?.data?.errorMsg || ax.message || "请求失败";
}

export async function fetchCaptcha(): Promise<{
  captchaId: string;
  imageBase64: string;
  expiresIn: number;
}> {
  const { data } = await api.get<ApiSuccess<{ captchaId: string; imageBase64: string; expiresIn: number }>>(
    "/api/merchant/auth/captcha"
  );
  if (!data.success) throw new Error("获取验证码失败");
  return data.data;
}

export async function login(payload: {
  account: string;
  password: string;
  captchaId: string;
  captchaCode: string;
  rememberMe?: boolean;
}): Promise<LoginResult> {
  try {
    const encryptedPassword = await encryptLoginPassword(payload.password);
    const { data } = await api.post<ApiSuccess<LoginResult>>("/api/merchant/auth/login", {
      ...payload,
      password: encryptedPassword,
    });
    if (!data.success) throw new Error("登录失败");
    setSession(data.data.token, data.data.user);
    return data.data;
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export async function logout(): Promise<void> {
  try {
    await api.post("/api/merchant/auth/logout", {});
  } finally {
    clearSession();
  }
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  try {
    const { data } = await api.post<ApiSuccess<{ ok: boolean }>>("/api/merchant/auth/password", {
      oldPassword,
      newPassword,
    });
    if (!data.success) throw new Error("修改密码失败");
    clearSession();
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export type PeriodMetricRow = {
  lastMonthTotalUsd: string;
  lastMonthToYesterdayUsd: string;
  mtdToYesterdayUsd: string;
  momDiffRatio: string;
  projectedMomDiffRatio: string;
};

export type ChannelCountryStats = {
  rows: Array<
    PeriodMetricRow & {
      channelId: string;
      country: string;
    }
  >;
  totals: PeriodMetricRow;
};

export type CountryStats = {
  rows: Array<PeriodMetricRow & { country: string }>;
  totals: PeriodMetricRow;
};

export type Cashflow30dStats = {
  totalOrders: number;
  totalUsd: string;
  series: Array<{
    channelId: string;
    points: Array<{ date: string; count: number }>;
  }>;
};

async function getStats<T>(path: string): Promise<T> {
  try {
    const { data } = await api.get<ApiSuccess<T>>(path);
    if (!data.success) throw new Error("获取统计失败");
    return data.data;
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export function fetchChannelCountryStats() {
  return getStats<ChannelCountryStats>("/api/merchant/stats/channel-country");
}

export function fetchCountryStats() {
  return getStats<CountryStats>("/api/merchant/stats/country");
}

export function fetchCashflow30dStats() {
  return getStats<Cashflow30dStats>("/api/merchant/stats/cashflow-30d");
}

export type PaymentOrderListItem = {
  platformOrderId: string;
  merchantId: string;
  merchantOrder: string;
  country: string;
  price: string;
  currency: string;
  usdAmount: string | null;
  exchangeRate: string | null;
  paymentStatus: string;
  notifyStatus: string;
  channelId: string;
  channelName: string;
  channelTag: string;
  sandbox: number;
  channelOrderId: string;
  createdAt: string | null;
  paidAt: string | null;
  notifiedAt: string | null;
};

export type PaymentOrderListResult = {
  list: PaymentOrderListItem[];
  total: number;
  page: number;
  pageSize: number;
};

export type PaymentOrderListQuery = {
  page?: number;
  pageSize?: number;
  platformOrderId?: string;
  merchantOrder?: string;
  paymentStatus?: string;
  startDate?: string;
  endDate?: string;
};

export async function fetchPaymentOrders(
  query: PaymentOrderListQuery = {}
): Promise<PaymentOrderListResult> {
  try {
    const { data } = await api.get<ApiSuccess<PaymentOrderListResult>>(
      "/api/merchant/payment-orders",
      { params: query }
    );
    if (!data.success) throw new Error("获取支付订单失败");
    return data.data;
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export async function exportPaymentOrders(query: Omit<PaymentOrderListQuery, "page" | "pageSize"> = {}): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>("/api/merchant/payment-orders/export", {
      params: query,
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export type { MerchantUser };
